import type { CompiledAutomationActionBundle } from './automationDraftCompiler'
import type { AutomationActionExecutionEntry, AutomationActionExecutionResult } from '../../automationActionExecutionService'

type ExecutionActionState = 'SUCCESS' | 'FAILED'

export type AiMagicExecutionActionDetail = {
  actionId: string
  type: AutomationActionExecutionEntry['type']
  outcome: ExecutionActionState
  heading: string
  summary: string
  errorMessage?: string
}

export type AiMagicExecutionResultDetail = {
  runId: string
  outcome: AutomationActionExecutionResult['outcome']
  errorMessage: string | null
  warnings: string[]
  actions: AiMagicExecutionActionDetail[]
}

const toReadableActionType = (value: string) => value.toLowerCase().replaceAll('_', ' ')

const clipped = (value: string, max = 96) => {
  const normalized = value.trim().replace(/\s+/g, ' ')
  if (!normalized) return ''
  if (normalized.length <= max) return normalized
  return `${normalized.slice(0, max - 1).trimEnd()}…`
}

const quoted = (value: unknown) => {
  const text = typeof value === 'string' ? clipped(value) : ''
  return text ? `"${text}"` : ''
}

const describeAction = (entry: AutomationActionExecutionEntry, config: Record<string, unknown> | undefined) => {
  switch (entry.type) {
    case 'CREATE_NODE': {
      const nodeKind = typeof config?.nodeKind === 'string' ? config.nodeKind.toLowerCase() : 'node'
      const title = quoted(config?.title)
      const board = typeof config?.boardKey === 'string' ? config.boardKey : ''
      return {
        heading: `Create ${nodeKind}`,
        summary: [
          title ? `Create ${nodeKind} ${title}.` : `Create ${nodeKind}.`,
          board ? `Board: ${board}.` : '',
        ].filter(Boolean).join(' '),
      }
    }
    case 'CREATE_CHECKLIST_ITEM': {
      const title = quoted(config?.title)
      return {
        heading: 'Create checklist item',
        summary: title ? `Create checklist item ${title}.` : 'Create checklist item.',
      }
    }
    case 'ASSIGN_TASK': {
      const targetId = typeof config?.targetId === 'string' ? config.targetId : ''
      const assigneeId = typeof config?.assigneeId === 'string' ? config.assigneeId : ''
      return {
        heading: 'Assign work item',
        summary: [targetId ? `Target: ${targetId}.` : '', assigneeId ? `Assignee: ${assigneeId}.` : '']
          .filter(Boolean)
          .join(' ') || 'Assign the selected work item.',
      }
    }
    case 'COMPLETE_NODE': {
      const targetId = typeof config?.targetId === 'string' ? config.targetId : ''
      return {
        heading: 'Complete work item',
        summary: targetId ? `Mark ${targetId} complete.` : 'Mark the selected work item complete.',
      }
    }
    case 'UPDATE_FIELD': {
      const targetType = typeof config?.targetType === 'string' ? config.targetType.toLowerCase() : 'record'
      const fieldPath = typeof config?.fieldPath === 'string' ? config.fieldPath : 'field'
      const valueLabel = typeof config?.value === 'string'
        ? config.value
        : config?.value === null || config?.value === undefined
          ? 'empty'
          : JSON.stringify(config.value)
      return {
        heading: `Update ${fieldPath}`,
        summary: `Set ${targetType} ${fieldPath} to ${valueLabel}.`,
      }
    }
    case 'SEND_CHAT_MESSAGE': {
      const channelId = typeof config?.channelId === 'string' ? config.channelId : ''
      const body = quoted(config?.body ?? config?.text)
      return {
        heading: 'Send chat message',
        summary: [
          body ? `Message ${body}.` : 'Send chat message.',
          channelId ? `Channel: ${channelId}.` : '',
        ].filter(Boolean).join(' '),
      }
    }
    case 'LOG_TO_LEDGER': {
      const category = typeof config?.category === 'string' ? config.category : ''
      const message = quoted(config?.message)
      return {
        heading: 'Write ledger entry',
        summary: [
          category ? `Category: ${category}.` : '',
          message ? `Message ${message}.` : '',
        ].filter(Boolean).join(' ') || 'Write a ledger entry.',
      }
    }
    case 'NOTIFY_USER': {
      const title = quoted(config?.title)
      return {
        heading: 'Send notification',
        summary: title ? `Notification ${title}.` : 'Send notification.',
      }
    }
    case 'WEBHOOK':
      return {
        heading: 'Trigger webhook',
        summary: 'Trigger an outbound webhook.',
      }
    default:
      return {
        heading: toReadableActionType(entry.type),
        summary: `Run ${toReadableActionType(entry.type)}.`,
      }
  }
}

export const buildAiMagicExecutionResultDetail = (
  bundle: CompiledAutomationActionBundle,
  result: AutomationActionExecutionResult,
): AiMagicExecutionResultDetail => {
  const actionById = new Map(bundle.actions.map((action) => [action.id, action]))
  return {
    runId: result.runId,
    outcome: result.outcome,
    errorMessage: result.errorMessage,
    warnings: bundle.warnings,
    actions: result.entries.map((entry) => {
      const action = actionById.get(entry.actionId)
      const described = describeAction(entry, action?.config as Record<string, unknown> | undefined)
      return {
        actionId: entry.actionId,
        type: entry.type,
        outcome: entry.outcome,
        heading: described.heading,
        summary: described.summary,
        errorMessage: entry.errorMessage,
      }
    }),
  }
}
